import React from 'react';

import { useAsset, AssetKey } from '@ocx/data-configuration';
import { PaymentInstrumentType } from '@ocx/graphql';

export type TransactionPaymentInstrumentType = PaymentInstrumentType | 'CASH' | 'SPLIT_PAYMENT';

type IconStyles = React.CSSProperties | undefined;

const largeIconStyles: IconStyles = {
  width: 72,
  height: 51,
};

const mediumIconStyles: IconStyles = {
  width: 40,
  height: 28,
};

type PaymentInsrumentIconSize = 'small' | 'medium' | 'large';

export type PaymentInstrumentIconProps = {
  type: TransactionPaymentInstrumentType;
  /**
   * @default 'small'
   */
  size?: PaymentInsrumentIconSize;
};

const stylesBySizeMapping: { [key in PaymentInsrumentIconSize]?: IconStyles } = {
  medium: mediumIconStyles,
  large: largeIconStyles,
};

const assetMap: Partial<Record<TransactionPaymentInstrumentType, AssetKey>> = {
  [PaymentInstrumentType.Visa]: 'VisaIcon',
  [PaymentInstrumentType.ApplePayVisa]: 'VisaIcon',
  [PaymentInstrumentType.GooglePayVisa]: 'VisaIcon',

  [PaymentInstrumentType.Mastercard]: 'MastercardIcon',
  [PaymentInstrumentType.ApplePayMastercard]: 'MastercardIcon',
  [PaymentInstrumentType.GooglePayMastercard]: 'MastercardIcon',

  [PaymentInstrumentType.Discover]: 'DiscoverIcon',
  [PaymentInstrumentType.ApplePayDiscover]: 'DiscoverIcon',
  [PaymentInstrumentType.GooglePayDiscover]: 'DiscoverIcon',

  [PaymentInstrumentType.AmericanExpress]: 'AmexIcon',
  [PaymentInstrumentType.ApplePayAmericanExpress]: 'AmexIcon',
  [PaymentInstrumentType.GooglePayAmericanExpress]: 'AmexIcon',

  [PaymentInstrumentType.Prepaid]: 'PrepaidIcon',
  [PaymentInstrumentType.ApplePay]: 'ApplePayIcon',
  [PaymentInstrumentType.GooglePay]: 'GooglePayIcon',

  [PaymentInstrumentType.Credit]: 'GeneralCardIcon',
  [PaymentInstrumentType.Debit]: 'GeneralCardIcon',
  [PaymentInstrumentType.Unknown]: 'UnknownPaymentInstrumentIcon',

  CASH: 'CashIcon',
  SPLIT_PAYMENT: 'SplitPaymentIcon',
};

export const PaymentInstrumentIcon = ({ type, size = 'small' }: PaymentInstrumentIconProps) => {
  const fallbackKey: AssetKey = 'UnknownPaymentInstrumentIcon';

  const assetKey = assetMap[type] ?? fallbackKey;
  const iconSrc = useAsset(assetKey);

  if (!assetMap[type] || !iconSrc) {
    return null;
  }

  const shouldSkipStyling = type === PaymentInstrumentType.ApplePay || type === PaymentInstrumentType.GooglePay;

  const style = shouldSkipStyling ? undefined : stylesBySizeMapping[size];

  return <img src={iconSrc} alt={type} style={style} />;
};
